'use client';

import type { ReactNode } from 'react';
import { HStack } from '@terra/ui-styled-system';
import { SecurityCheckIcon, ShieldBanIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Button } from '../../../components/button';
import type { ScopeCounts, ScopeFilter } from './scope-types';

type ScopeFilterToolbarProps = {
  scopeFilter: ScopeFilter;
  scopeCounts: ScopeCounts;
  selectedCount: number;
  isDisabled: boolean;
  onFilterChange: (filter: ScopeFilter) => void;
  onBulkApprove: () => void;
  onBulkReject: () => void;
};

type BulkAction = {
  label: string;
  colorPalette: string;
  icon: ReactNode;
  onClick: () => void;
};

export function ScopeFilterToolbar({
  scopeFilter,
  scopeCounts,
  selectedCount,
  isDisabled,
  onFilterChange,
  onBulkApprove,
  onBulkReject,
}: ScopeFilterToolbarProps) {
  const t = useTranslations('designSystem.networkSettings.externalScope');
  const filterTabs: { key: ScopeFilter; label: string }[] = [
    { key: 'all', label: t('filterAll') },
    { key: 'PENDING', label: t('filterPending') },
    { key: 'IN_SCOPE', label: t('filterInScope') },
    { key: 'OUT_OF_SCOPE', label: t('filterOutOfScope') },
  ];
  const approveAction: BulkAction = {
    label: t('approve'),
    colorPalette: 'green',
    icon: <SecurityCheckIcon size={12} />,
    onClick: onBulkApprove,
  };
  const rejectAction: BulkAction = {
    label: t('reject'),
    colorPalette: 'red',
    icon: <ShieldBanIcon size={12} />,
    onClick: onBulkReject,
  };
  const bulkActions =
    scopeFilter === 'IN_SCOPE'
      ? [rejectAction]
      : scopeFilter === 'OUT_OF_SCOPE'
        ? [approveAction]
        : [approveAction, rejectAction];

  return (
    <HStack gap={3} mb={3} alignItems="center" flexWrap="wrap">
      <HStack gap={1} alignItems="center">
        {filterTabs.map((tab) => (
          <Button
            key={tab.key}
            size="xs"
            variant={scopeFilter === tab.key ? 'subtle' : 'ghost'}
            onClick={() => onFilterChange(tab.key)}
          >
            {`${tab.label} ${scopeCounts[tab.key]}`}
          </Button>
        ))}
      </HStack>
      <HStack gap={1} alignItems="center" marginLeft="auto">
        {bulkActions.map((action) => (
          <Button
            key={action.label}
            size="xs"
            variant="ghost"
            colorPalette={action.colorPalette}
            disabled={selectedCount === 0 || isDisabled}
            onClick={action.onClick}
          >
            {action.icon}
            {selectedCount > 0
              ? `${action.label} (${selectedCount})`
              : action.label}
          </Button>
        ))}
      </HStack>
    </HStack>
  );
}
